import { Toast } from '@base-ui/react/toast'
import { flushPendingRemovals, setNoticeSink, type Notice } from '@church/data'
import { faCircleCheck, faCircleInfo, faTriangleExclamation, faXmark } from '@church/ui-icons'
import { useEffect } from 'react'
import { Icon } from './ui.tsx'

/**
 * Notices (`notify` in @church/data), drawn as toasts: a dark pill at the
 * bottom, above the phone's tab bar, saying what just happened and offering
 * one action (Undo, Retry). New ones stack on top; a swipe or Esc dismisses;
 * hovering pauses the timer so there is time to read and reach Undo.
 */
let count = 0
const manager = Toast.createToastManager<{ tone: NonNullable<Notice['tone']> }>()

const TONE_ICONS = {
  success: faCircleCheck,
  info: faCircleInfo,
  error: faTriangleExclamation,
} as const

const TONE_CLASSES = {
  success: 'text-success-solid',
  info: 'text-content-inverted',
  error: 'text-danger-solid',
} as const

export function ToastProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    setNoticeSink({
      show: (notice) => {
        const id = notice.id ?? `notice-${++count}`
        return manager.add({
          id,
          title: notice.title,
          description: notice.description,
          timeout: notice.timeout ?? 5000,
          priority: notice.tone === 'error' ? 'high' : 'low',
          data: { tone: notice.tone ?? 'success' },
          actionProps: notice.action
            ? {
                children: notice.action.label,
                // Undo, Retry, Open: done, so the notice goes with it.
                onClick: () => {
                  notice.action?.run()
                  manager.close(id)
                },
              }
            : undefined,
        })
      },
      close: (id) => manager.close(id),
    })
    // A removal waiting out its Undo window is sent before the page goes.
    const hidden = () => {
      if (document.visibilityState === 'hidden') flushPendingRemovals()
    }
    window.addEventListener('pagehide', flushPendingRemovals)
    document.addEventListener('visibilitychange', hidden)
    return () => {
      setNoticeSink(null)
      window.removeEventListener('pagehide', flushPendingRemovals)
      document.removeEventListener('visibilitychange', hidden)
    }
  }, [])

  return (
    <Toast.Provider toastManager={manager} limit={3}>
      {children}
      <Toast.Portal>
        <Toast.Viewport className="toast-viewport vt-toasts fixed inset-x-0 z-70 mx-auto flex w-full max-w-md flex-col gap-2 px-3">
          <ToastList />
        </Toast.Viewport>
      </Toast.Portal>
    </Toast.Provider>
  )
}

function ToastList() {
  const { toasts } = Toast.useToastManager<{ tone: NonNullable<Notice['tone']> }>()
  return toasts.map((toast) => {
    const tone = toast.data?.tone ?? 'success'
    return (
      <Toast.Root
        key={toast.id}
        toast={toast}
        swipeDirection={['down', 'right']}
        className="toast flex items-start gap-3 rounded-card bg-surface-inverted py-3 pr-2 pl-4 text-content-inverted shadow-2xl"
      >
        <Icon icon={TONE_ICONS[tone]} className={`mt-0.5 size-4 ${TONE_CLASSES[tone]}`} />
        <Toast.Content className="min-w-0 flex-1">
          <Toast.Title className="text-sm font-medium" />
          <Toast.Description className="mt-0.5 text-sm opacity-80" />
        </Toast.Content>
        {toast.actionProps && (
          <Toast.Action className="-my-1 shrink-0 rounded-control px-3 py-1.5 text-sm font-semibold text-content-inverted underline-offset-2 transition-colors duration-(--duration-fast) hover:bg-content-inverted/10 hover:underline" />
        )}
        <Toast.Close
          aria-label="Dismiss"
          className="-my-1 inline-flex size-8 shrink-0 items-center justify-center rounded-control opacity-70 transition-opacity hover:opacity-100"
        >
          <Icon icon={faXmark} className="size-3.5" />
        </Toast.Close>
      </Toast.Root>
    )
  })
}
